/**
 * ==== STANDALONE:FARMHAND_LITE:START ====
 * Feature: Offline voice note queue with IndexedDB storage and auto-sync
 * Tier: Lite ($3/mo)
 * Dependencies: IndexedDB (browser API), portalAPI
 * Generalization: Rename DB_NAME from 'redbridgeFarmhand' to 'farmhand'.
 *   Update sync endpoint from /portal/ to standalone API path.
 * ============================================
 *
 * Offline Voice Note Queue — IndexedDB-backed storage for voice notes
 * recorded without connectivity. Auto-syncs when online.
 */

import { pollTranscriptionStatus } from '../utils/voiceTranscription';

const DB_NAME = 'redbridgeFarmhand';
const DB_VERSION = 1;
const STORE_NAME = 'offlineNotes';
const MAX_RETRIES = 5;

export interface OfflineVoiceNote {
  id: string;
  audio_blob: Blob;
  coordinates: { latitude: number; longitude: number; accuracy: number; timestamp: string } | null;
  recorded_at: string;
  batch_id: string | null;
  plan_id: string | null;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  retry_count: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveOfflineNote(note: OfflineVoiceNote): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(note);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllPending(): Promise<OfflineVoiceNote[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.getAll('pending');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllNotes(): Promise<OfflineVoiceNote[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingCount(): Promise<number> {
  const notes = await getAllPending();
  return notes.length;
}

export async function getFailedNotes(): Promise<OfflineVoiceNote[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.objectStore(STORE_NAME).index('status');
    const request = index.getAll('failed');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function updateNoteStatus(id: string, status: OfflineVoiceNote['status'], retryCount?: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const note = getReq.result;
      if (note) {
        note.status = status;
        if (retryCount !== undefined) note.retry_count = retryCount;
        store.put(note);
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteNote(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function retryFailedNote(id: string): Promise<void> {
  await updateNoteStatus(id, 'pending', 0);
  await syncOfflineQueue();
}

/** Quick offline gate — only checks navigator.onLine.
 *  Real connectivity is verified by the actual API calls during sync. */
function isOnline(): boolean {
  return navigator.onLine;
}

// Prevent concurrent sync runs
let syncing = false;

export async function syncOfflineQueue(): Promise<number> {
  if (syncing) return 0;
  syncing = true;

  let syncedCount = 0;
  try {
    if (!isOnline()) return 0;

    const pending = await getAllPending();
    if (!pending.length) return 0;

    const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/?$/, '');

    for (const note of pending) {
      try {
        await updateNoteStatus(note.id, 'syncing');

        // Step 1: Transcribe audio
        const formData = new FormData();
        formData.append('audio', note.audio_blob, 'recording.webm');
        formData.append('context', 'farm_log');

        const transcribeResp = await fetch(`${API_BASE}/api/voice/transcribe/`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!transcribeResp.ok) throw new Error('Transcription failed');
        const initial = await transcribeResp.json();
        const transcribeResult = initial?.job_id
          ? await pollTranscriptionStatus(API_BASE, initial.status_url || `/api/voice/transcribe/status/${initial.job_id}/`)
          : initial;
        const text = (transcribeResult.text as string) || '';

        // Step 2: Parse with Laura
        const parseResp = await fetch(`${API_BASE}/api/planner/portal/smart-voice-note/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transcription: text }),
        });

        if (!parseResp.ok) throw new Error('Parse failed');
        const parseResult = await parseResp.json();

        // Step 3: Auto-save all entries that have a batch_id
        const entries = (parseResult.parsed_entries || []).filter(
          (e: any) => e.batch_id !== null
        );
        const tasks = parseResult.parsed_tasks || [];
        const completions = (parseResult.parsed_completions || []).filter(
          (tc: any) => tc.task_id !== null && tc.confidence >= 0.5
        );

        if (entries.length > 0 || tasks.length > 0 || completions.length > 0) {
          const saveResp = await fetch(`${API_BASE}/api/planner/portal/smart-voice-note/save/`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              entries: entries.map((e: any) => ({ ...e, selected: true })),
              tasks: tasks.map((t: any) => ({ ...t, selected: true })),
              task_completions: completions.map((tc: any) => ({ ...tc, selected: true })),
              transcription: text,
              recorded_at: note.recorded_at,
              ...(note.coordinates ? {
                gps_latitude: note.coordinates.latitude,
                gps_longitude: note.coordinates.longitude,
                gps_accuracy_meters: note.coordinates.accuracy,
              } : {}),
            }),
          });

          if (!saveResp.ok) throw new Error('Save failed');
        }

        // Success — mark synced and remove from DB
        await updateNoteStatus(note.id, 'synced');
        await deleteNote(note.id);
        syncedCount++;
      } catch (err) {
        console.error(`Failed to sync note ${note.id}:`, err);
        const newRetry = note.retry_count + 1;
        if (newRetry >= MAX_RETRIES) {
          await updateNoteStatus(note.id, 'failed', newRetry);
          showFailureToast('Voice note failed to sync after multiple attempts. Tap to review.', true);
        } else {
          await updateNoteStatus(note.id, 'pending', newRetry);
          showFailureToast(`Voice note sync retry ${newRetry}/${MAX_RETRIES}…`, false);
        }
      }
    }
  } finally {
    syncing = false;
  }

  return syncedCount;
}

/** Register sync triggers — call once from app root */
export function registerSyncTriggers(): void {
  // Trigger 1: App foregrounded / visibility change
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      syncOfflineQueue().then(count => {
        if (count > 0) showSyncToast(count);
      });
    }
  });

  // Trigger 2: Online event
  window.addEventListener('online', () => {
    setTimeout(() => {
      syncOfflineQueue().then(count => {
        if (count > 0) showSyncToast(count);
      });
    }, 2000);
  });

  // Trigger 3: Background Sync (Android bonus)
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    // Registration happens when saving offline — see saveOfflineNote usage
  }

  // Initial sync on load
  syncOfflineQueue().then(count => {
    if (count > 0) showSyncToast(count);
  });
}

/** Request background sync registration */
export async function requestBackgroundSync(): Promise<void> {
  try {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await (reg as any).sync.register('voice-note-sync');
    }
  } catch {
    // Background sync not supported — fine, we have other triggers
  }
}

function showSyncToast(count: number): void {
  // Dispatch a custom event that the UI can listen for
  window.dispatchEvent(new CustomEvent('offline-notes-synced', { detail: { count } }));
}

function showFailureToast(message: string, permanent: boolean): void {
  window.dispatchEvent(new CustomEvent('offline-note-sync-failed', {
    detail: { message, permanent },
  }));
}
// ==== STANDALONE:FARMHAND_LITE:END ====
